import { Config } from '@/environment';

import { RewardsBanner, LoyaltyInterruptModal, UpsellItem, GuacMode, SystemFlags } from '@/models/cms';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { SubmitOrderRequestPaymentMethod, SubmitOrderRequestPaymentMethodType } from '@/models/order';
import { CountryCode } from '@/store/ui';

export interface ConfigService {
  isLocal(): boolean;
  getBuyGiftCardsUrl(): string;
  getSupportUrl(): string;
  getCateringUrl(): string;
  getFaqUrl(): string;
  getRecaptchaKey(): string;
  getBuild(): string;
  getHomePagePromoBannerCanada(): RewardsBanner;
  getHomePageRewardsBanner(): RewardsBanner;
  getRewardsPageGuacModeRewardsBanner(): RewardsBanner;
  getRewardsPageGuestUnenrolledRewardsBanner(): RewardsBanner;
  getLoyaltyInterruptModal(): LoyaltyInterruptModal;
  getDynamicUpsellItems(orderType?: string): UpsellItem[];
  setDynamicUpsellItems(upsellItemsPickup: UpsellItem[], upsellItemsDelivery: UpsellItem[]): void;
  getSystemFlags(): SystemFlags;
  getHeaderPages(): string[];
  getGuacMode(): GuacMode | null;
  getPayeezyJsUrl(): string;
  getChaseJsUrl(): string;
  getChaseBaseUrl(): string;
  getTwitterAchievementBaseUrl(): string;
  getAllergenStatementUrl(): string;
  getSessionFrameUrl(): string;
  getRealFoodprintUrl(): string;
  getHowGoodUrl(): string;
  getRewardsPath(): string;
  getRefreshPointsThrottleTimeInMinutes(): number;
  getIsStubPayment(): boolean;
  getStubPayment(): SubmitOrderRequestPaymentMethod;
}

export class AppConfigService implements ConfigService {
  constructor(private config: Config, protected store: Store<AppState>) {}

  isLocal(): boolean {
    return this.config.isLocal;
  }

  getBuyGiftCardsUrl(): string {
    return this.config.urls.buyGiftCards;
  }

  getSupportUrl(): string {
    return this.config.urls.support;
  }

  getCateringUrl(): string {
    return this.config.urls.catering;
  }

  getFaqUrl(): string {
    return this.config.urls.faq;
  }

  getRecaptchaKey(): string {
    return this.config.recaptcha.key;
  }

  getBuild(): string {
    return this.config.build;
  }

  getHomePagePromoBannerCanada(): RewardsBanner {
    return this.config.cms.homePagePromoBannerCanada;
  }

  getHomePageRewardsBanner(): RewardsBanner {
    return this.config.cms.homePageRewardsBanner;
  }

  getRewardsPageGuacModeRewardsBanner(): RewardsBanner {
    return this.config.cms.rewardsPageGuacModeRewardsBanner;
  }

  getRewardsPageGuestUnenrolledRewardsBanner(): RewardsBanner {
    return this.config.cms.rewardsPageGuestUnenrolledRewardsBanner;
  }

  getLoyaltyInterruptModal(): LoyaltyInterruptModal {
    return this.config.cms.loyaltyInterruptModal;
  }

  getDynamicUpsellItems(orderType: string = 'pickup'): UpsellItem[] {
    if (orderType === 'pickup') {
      return this.config.cms.dynamicUpsellItemsPickup || this.config.cms.dynamicUpsellItems;
    } else {
      return this.config.cms.dynamicUpsellItemsDelivery || this.config.cms.dynamicUpsellItems;
    }
  }

  setDynamicUpsellItems(upsellItemsPickup: UpsellItem[], upsellItemsDelivery: UpsellItem[]) {
    this.config.cms.dynamicUpsellItemsPickup = upsellItemsPickup;
    this.config.cms.dynamicUpsellItemsDelivery = upsellItemsDelivery;
  }

  getSystemFlags() {
    return this.config.cms.systemFlags;
  }

  getHeaderPages() {
    return this.config.cms.systemFlags.headerPages;
  }

  getGuacMode(): GuacMode | null {
    return (this.store.state.featureFlags.orderingStatus?.isGuacModeActive && this.config.cms.guacMode) || null;
  }

  getPayeezyJsUrl(): string {
    return this.config.payeezy.jsLibrary;
  }

  getChaseBaseUrl(): string {
    return this.config.chase.baseFormUrl;
  }

  getChaseJsUrl(): string {
    return this.config.chase.jsLibraryUrl;
  }

  getSessionFrameUrl(): string {
    return this.config.urls.sessionFrameUrl;
  }

  getRealFoodprintUrl(): string {
    return this.config.urls.realFoodprint;
  }

  getHowGoodUrl(): string {
    return this.config.urls.howGood;
  }

  getRewardsPath(): string {
    return this.config.urls.rewardsPath;
  }

  getTwitterAchievementBaseUrl(): string {
    return this.config.urls.twitterAchievementBaseUrl;
  }

  getAllergenStatementUrl(): string {
    return this.config.urls.allergenStatementUrl;
  }

  getRefreshPointsThrottleTimeInMinutes(): number {
    return this.config.services.refreshPointsThrottleTimeInMinutes ?? 0;
  }

  getIsStubPayment(): boolean {
    return this.config.isStubPayment;
  }

  getStubPayment(): SubmitOrderRequestPaymentMethod {
    const isCASite = this.store.state.ui.countryProfile.countryCode === CountryCode.CA;
    const payment: SubmitOrderRequestPaymentMethod = {
      cardHolderName: this.config.stubPaymentInformation.cardHolderName,
      creditCardExpiration: this.config.stubPaymentInformation.creditCardExpiration,
      creditCardSingleUseToken: this.config.stubPaymentInformation.creditCardSingleUseToken,
      creditCardType: this.config.stubPaymentInformation.creditCardType,
      creditCardZipCode: this.config.stubPaymentInformation.creditCardZipCode,
      lastFourAccountNumbers: this.config.stubPaymentInformation.lastFourAccountNumbers,
      paymentProviderId:  !isCASite ? this.config.stubPaymentInformation.paymentProviderId : this.config.stubPaymentInformation.paymentProviderId_CA,
      paymentType: SubmitOrderRequestPaymentMethodType[this.config.stubPaymentInformation.paymentType as keyof typeof SubmitOrderRequestPaymentMethodType]
    };

    if(!isNaN(this.config.stubPaymentInformation.chipotleWalletId as number)) {
      payment.chipotleWalletId = this.config.stubPaymentInformation.chipotleWalletId;
    }

    return payment;
  }
}
